// The Studio's front page, and the whole user flow in one screen:
// drop photos -> choose which of them are in the batch -> pick how many
// variations of each -> Run. Run drives exactly the sequence
// 08-contracts.md #1 describes: the client generates task_id and N seeds,
// uploads each photo (POST /artifacts, then PUT to Store with the returned
// token), then POST /tasks with the image and seed arrays.
//
// Two columns on purpose: the contact sheet of what you are about to spend
// on (left, the wide side), and the console that decides the batch (right,
// sticky, so the price and the Run button stay in view while you scroll a
// long sheet). Nothing about the batch is decided in a modal.
//
// Selection is component state, not a field on the upload plan: the plan
// and its sequencing are pure logic with their own unit tests
// (lib/generate.ts), and "which of these do I actually want" is a view
// concern that changes nothing about how a chosen photo is uploaded.
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import { useLocation } from "react-router-dom";
import {
  allocateArtifact,
  createTask,
  getArtifact,
  warm,
  listAliases,
  quoteAlias,
  type Alias,
} from "../api/client";
import { uploadToStore, fetchStoreObjectURL } from "../api/store";
import {
  allUploadsDone,
  committedArtifactIds,
  generateVariantSeeds,
  initialQuoteState,
  newTaskId,
  onQuoteLoaded,
  onResultsChanged,
  resultCount,
  setStepStatus,
  type UploadStep,
  CLIP_FPS,
  CLIP_SECONDS,
  clipFrames,
  imageFrameFor,
  type PhotoAt,
} from "../lib/generate";
import { formatMinor } from "../lib/format";
import { availabilityView, useAvailability } from "../lib/availability";
import {
  clearSession,
  loadSession,
  memoryFor,
  nextIndex,
  restoredSteps,
  runSignature,
  saveSession,
  sessionPhotos,
  type SessionRun,
} from "../lib/genSession";
import RunStrip from "../components/RunStrip";
import {
  IMAGE_TO_VIDEO,
  formatCost,
  formatWait,
  fpsFor,
  gradeChoices,
  secondsFor,
  targetsFor,
  useCatalog,
} from "../lib/catalog";
import { useAuth } from "../auth/AuthContext";

/** What the prompt box says while the enhancer will write the prompt (ADR-0057). */
const ENHANCE_PLACEHOLDER = "Leave empty to let the enhancer describe the motion from the photo";

const GENERATE_TEMPLATES = new Set(["photo_to_video", "echo"]);

interface PrefillState {
  alias?: string;
  artifactId?: string;
  artifactLabel?: string;
  downloadUrl?: string;
  downloadToken?: string;
  hint?: string;
}

// Warm hints (ADR-0030): one per alias per minute, module-level so a
// re-mounted view does not re-send at once.
const WARM_RESEND_MS = 60_000;
const warmSentAt = new Map<string, number>();

// Uploads outlive the view: one that finishes after the user left the tab
// is recorded here and applied when Generate mounts again.
const inflight = new Set<number>();
const finished = new Map<number, Partial<UploadStep>>();

function reconcileUploads(steps: UploadStep[]): UploadStep[] {
  return steps.map((s) => {
    const done = finished.get(s.index);
    if (done) {
      finished.delete(s.index);
      return { ...s, ...done };
    }
    // An upload that was running when the page went away is started again.
    if (s.status === "uploading" && !inflight.has(s.index)) return { ...s, status: "pending" as const };
    return s;
  });
}

// The view mounts once the signed-in user is known, keyed by that user, so
// its session (lib/genSession.ts) is always loaded for the right owner and a
// different user at the same browser gets a fresh view.
export default function Generate() {
  const { user } = useAuth();
  if (!user) return null;
  return <GenerateView key={user.user_id} />;
}

function GenerateView() {
  const { user, refresh } = useAuth();
  const location = useLocation();
  const prefill = location.state as PrefillState | undefined;

  const [aliases, setAliases] = useState<Alias[]>([]);
  const [aliasesError, setAliasesError] = useState<string | null>(null);
  // `model` is what the Model list shows: IMAGE_TO_VIDEO for the grades
  // (whose alias follows the chosen resolution), or an alias name.
  // The session (lib/genSession.ts): restored from memory or storage, saved
  // on every change below, emptied only by Clear.
  const owner = user?.user_id ?? "";
  const [saved] = useState(() => loadSession(owner));
  const [mem] = useState(() => memoryFor(owner));
  const [model, setModel] = useState<string>(prefill?.alias ?? saved?.model ?? "");
  const [grade, setGrade] = useState(saved?.grade ?? "");
  const [hint, setHint] = useState(prefill?.hint ?? saved?.hint ?? "");
  const [enhance, setEnhance] = useState(true);
  const [photoAt, setPhotoAt] = useState<PhotoAt>("start");
  const [variants, setVariants] = useState(saved?.variants ?? 1);
  const [seconds, setSeconds] = useState(saved?.seconds ?? 5);
  const [fps, setFps] = useState(saved?.fps ?? 24);
  const catalog = useCatalog();
  const [steps, setSteps] = useState<UploadStep[]>(() =>
    reconcileUploads(mem?.steps ?? restoredSteps(saved?.photos ?? [])),
  );
  const [deselected, setDeselected] = useState<Set<number>>(() => new Set(saved?.deselected ?? []));
  const [runs, setRuns] = useState<SessionRun[]>(saved?.runs ?? []);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const [quote, setQuote] = useState(initialQuoteState(0));
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [previewURLs, setPreviewURLs] = useState<Record<number | string, string>>(() => mem?.previews ?? {});

  useEffect(() => {
    saveSession(
      owner,
      { model, grade, seconds, fps, hint, variants, photos: sessionPhotos(steps), deselected: [...deselected], runs },
      steps,
      previewURLs,
    );
  }, [owner, model, grade, seconds, fps, hint, variants, steps, deselected, runs, previewURLs]);

  // A photo restored after a reload has no local bytes: fetch its preview.
  useEffect(() => {
    for (const s of steps) {
      const aid = s.artifactId;
      if (!aid || previewURLs[s.index] || previewURLs[aid]) continue;
      getArtifact(aid)
        .then((v) =>
          v.download_url && v.download_token ? fetchStoreObjectURL(v.download_url, v.download_token) : undefined,
        )
        .then((url) => url && setPreviewURLs((p) => ({ ...p, [aid]: url })))
        .catch(() => undefined);
    }
    // only steps added since; previews filled in are skipped above
  }, [steps]);

  // Every added photo uploads at once, one at a time, so the session can be
  // restored from its artifact ids; Run only waits for the chosen ones.
  useEffect(() => {
    if (steps.some((s) => s.status === "uploading")) return;
    const next = steps.find((s) => s.status === "pending" && s.file.size > 0);
    if (!next) return;
    const index = next.index;
    inflight.add(index);
    const patch = (p: Partial<UploadStep>) => {
      if (mounted.current) setSteps((prev) => setStepStatus(prev, index, p));
      else finished.set(index, p);
    };
    setSteps((prev) => setStepStatus(prev, index, { status: "uploading", error: undefined }));
    (async () => {
      try {
        const contentType = next.file.type || "application/octet-stream";
        const alloc = await allocateArtifact(contentType, next.file.size);
        await uploadToStore(alloc.upload_url, alloc.upload_token, next.file, contentType);
        patch({ status: "done", artifactId: alloc.artifact_id });
      } catch (e) {
        patch({ status: "error", error: e instanceof Error ? e.message : "upload failed" });
      } finally {
        inflight.delete(index);
      }
    })();
  }, [steps]);

  useEffect(() => {
    listAliases()
      .then((all) => {
        const usable = all.filter((a) => a.ui_template && GENERATE_TEMPLATES.has(a.ui_template));
        setAliases(usable);
      })
      .catch((e) => setAliasesError(e instanceof Error ? e.message : "failed to load models"));
  }, []);

  // Prefilled "use as input" from Task Detail / Prompt ideas: adds a
  // synthetic already-uploaded step so it counts toward the batch without
  // being re-uploaded.
  useEffect(() => {
    if (!prefill?.artifactId) return;
    const placeholder = new File([], prefill.artifactLabel ?? "reused-artifact");
    setSteps((prev) => [
      ...prev,
      { index: prev.length, file: placeholder, status: "done", artifactId: prefill.artifactId },
    ]);
    if (prefill.downloadUrl && prefill.downloadToken) {
      fetchStoreObjectURL(prefill.downloadUrl, prefill.downloadToken)
        .then((url) => setPreviewURLs((p) => ({ ...p, [prefill.artifactId as string]: url })))
        .catch(() => undefined);
    }
  }, []);

  // ADR-0047: only the Catalog's exposed aliases are offered (all of them
  // until the catalog arrives, or when it lists none).
  const exposed = catalog?.features.aliases;
  const offered = useMemo(
    () => (exposed && exposed.length > 0 ? aliases.filter((a) => exposed.includes(a.name)) : aliases),
    [aliases, exposed],
  );
  const grades = useMemo(() => gradeChoices(catalog, offered.map((a) => a.name)), [catalog, offered]);
  const models = useMemo(
    () => [
      ...(grades.length > 0 ? [{ value: IMAGE_TO_VIDEO, label: "Image to video" }] : []),
      ...offered.filter((a) => !grades.some((g) => g.alias === a.name)).map((a) => ({ value: a.name, label: a.name })),
    ],
    [grades, offered],
  );
  useEffect(() => {
    // A prefilled grade alias ("use as input") opens Image to video at that grade.
    const g = grades.find((x) => x.alias === model);
    if (g) {
      setGrade(g.grade);
      setModel(IMAGE_TO_VIDEO);
    } else if (models.length > 0 && !models.some((m) => m.value === model)) {
      setModel(models[0].value);
    }
  }, [grades, models, model]);
  useEffect(() => {
    if (model === IMAGE_TO_VIDEO && grades.length > 0 && !grades.some((g) => g.grade === grade)) setGrade(grades[0].grade);
  }, [model, grades, grade]);
  const aliasName = model === IMAGE_TO_VIDEO ? (grades.find((g) => g.grade === grade)?.alias ?? "") : model;
  const alias = useMemo(() => offered.find((a) => a.name === aliasName), [offered, aliasName]);
  const supportsSeed = alias ? "seed" in (alias.inputs ?? {}) : false;
  const supportsPrompt = alias ? "prompt" in (alias.inputs ?? {}) : false;
  const aliasInputs = (alias?.inputs ?? {}) as Record<string, { optional?: boolean } | undefined>;
  // A Preset without a condition step needs the user's prompt; elsewhere it
  // stays an optional hint.
  const promptRequiredByAlias = supportsPrompt && !aliasInputs.prompt?.optional;
  // ADR-0055: the grades rewrite the prompt from the photo first; on by default.
  const supportsEnhance = "enhance" in aliasInputs;
  // ADR-0059: the grades can place the photo at the last or a middle frame.
  const supportsPhotoAt = "image_frame" in aliasInputs;
  const supportsLength = "frames" in aliasInputs;
  const supportsFps = "fps" in aliasInputs;
  // ADR-0047: an alias the Catalog offers targets for picks one of them
  // (size, length, frame rate, price); the free length/rate pickers stay for
  // the aliases it does not list.
  const targets = useMemo(() => targetsFor(catalog, aliasName), [catalog, aliasName]);
  const target = targets.find((t) => t.seconds === seconds && t.fps === fps);
  const lengths = useMemo(() => secondsFor(catalog, aliasName), [catalog, aliasName]);
  const rates = useMemo(() => fpsFor(catalog, aliasName, seconds), [catalog, aliasName, seconds]);
  useEffect(() => {
    // Keep the length when the new resolution or rate offers it; else the first target.
    if (targets.length > 0 && !target) {
      const sameLength = targets.find((t) => t.seconds === seconds);
      const t = sameLength ?? targets[0];
      setSeconds(t.seconds);
      setFps(t.fps);
    }
  }, [targets, target, seconds]);
  // ADR-0047: a target alias without a prompt runs the Catalog's safe default.
  const defaultPrompt = targets.length > 0 ? catalog?.default_prompt || "" : "";
  const promptRequired = promptRequiredByAlias && !defaultPrompt;
  // ADR-0057: with the enhancer on, an empty prompt is written from the photo;
  // the default runs only when it is off (or every provider fails).
  const enhancing = supportsEnhance && enhance;
  const availability = useAvailability(aliasName);
  const avail = availability ? availabilityView(availability.state, target) : undefined;

  const inBatch = useCallback((s: UploadStep) => !deselected.has(s.index), [deselected]);
  const selected = steps.filter(inBatch);
  const photoCount = selected.length;
  const effectiveVariants = supportsSeed ? variants : 1;
  const results = resultCount(photoCount, effectiveVariants);

  useEffect(() => {
    setQuote((q) => onResultsChanged(q, results));
  }, [results]);

  // ADR-0030 warm hint: photos are selected and a model is chosen, so a
  // click is likely. Sent when that becomes true and again every
  // WARM_RESEND_MS while it stays true (Core's hint lives 120 s), at most
  // once per WARM_RESEND_MS per alias across re-renders. Best effort.
  useEffect(() => {
    if (!alias || results <= 0) return;
    const send = () => {
      const last = warmSentAt.get(alias.name) ?? 0;
      if (Date.now() - last < WARM_RESEND_MS) return;
      warmSentAt.set(alias.name, Date.now());
      warm(alias.name).catch(() => undefined);
    };
    send();
    const timer = window.setInterval(send, WARM_RESEND_MS);
    return () => window.clearInterval(timer);
  }, [alias, results]);

  useEffect(() => {
    if (!alias || results <= 0) return;
    let cancelled = false;
    // A grade prices each result by its length (ADR-0041); the cleanup's
    // `cancelled` drops a response for a length the user has moved off.
    (supportsLength
      ? quoteAlias(alias.name, results, seconds, supportsFps ? fps : undefined)
      : quoteAlias(alias.name, results))
      .then((q) => {
        if (!cancelled) setQuote((prev) => onQuoteLoaded(prev, results, q));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [alias, results, supportsLength, supportsFps, seconds, fps]);

  const addFiles = useCallback((files: FileList | File[]) => {
    const list = Array.from(files);
    setSteps((prev) => {
      const startIndex = nextIndex(prev);
      const added = list.map((file, i) => ({ index: startIndex + i, file, status: "pending" as const }));
      for (const s of added) {
        setPreviewURLs((p) => ({ ...p, [s.index]: URL.createObjectURL(s.file) }));
      }
      return [...prev, ...added];
    });
  }, []);

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
  }

  const uploading = steps.some((s) => s.status === "uploading");
  const uploadedCount = steps.filter((s) => s.status === "done").length;

  function toggle(index: number) {
    setDeselected((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }

  function setAll(keep: boolean) {
    setDeselected(keep ? new Set() : new Set(steps.map((s) => s.index)));
  }

  function removePhoto(index: number) {
    const url = previewURLs[index];
    if (url?.startsWith("blob:")) URL.revokeObjectURL(url);
    setSteps((prev) => prev.filter((s) => s.index !== index));
    setDeselected((prev) => {
      const next = new Set(prev);
      next.delete(index);
      return next;
    });
  }

  function retryPhoto(index: number) {
    setSteps((prev) => setStepStatus(prev, index, { status: "pending", error: undefined }));
  }

  /** Empty the session: photos, prompt, settings and the runs strip. */
  function onClear() {
    clearSession(owner);
    setSteps([]);
    setDeselected(new Set());
    setRuns([]);
    setHint("");
    setVariants(1);
    setPreviewURLs({});
    setRunError(null);
  }

  // What Run would submit, and whether exactly that already ran here.
  const selectedReady = selected.length > 0 && allUploadsDone(selected);
  const effectivePrompt = supportsPrompt ? hint.trim() || defaultPrompt : "";
  const signature = useMemo(() => {
    if (!alias || !selectedReady) return "";
    return runSignature({
      alias: alias.name,
      imageIds: committedArtifactIds(selected),
      seconds: supportsLength ? seconds : undefined,
      fps: supportsFps ? fps : undefined,
      prompt: effectivePrompt,
      variants: effectiveVariants,
      enhance: supportsEnhance ? enhance : undefined,
      photoAt: supportsPhotoAt ? photoAt : undefined,
    });
  }, [alias, selectedReady, selected, supportsLength, supportsFps, seconds, fps, effectivePrompt, effectiveVariants, supportsEnhance, enhance, supportsPhotoAt, photoAt]);
  const alreadyRun = signature !== "" && runs.some((r) => r.signature === signature);

  async function onRun() {
    if (!alias || !selectedReady) return;
    setRunError(null);
    setRunning(true);
    try {
      const imageIds = committedArtifactIds(selected);
      const inputs: Record<string, unknown> = { image: imageIds };
      if (supportsSeed) inputs.seed = generateVariantSeeds(effectiveVariants);
      if (supportsPrompt && hint.trim()) inputs.prompt = hint.trim();
      if (supportsEnhance && !enhance) inputs.enhance = "off";
      const imageFrame = supportsPhotoAt ? imageFrameFor(photoAt, seconds) : undefined;
      if (imageFrame !== undefined) inputs.image_frame = imageFrame;
      // Length in seconds at the 24 fps base (DFR renders 24 fps and doubles
      // it for 48); Core refuses what the grade cannot serve (ADR-0041).
      if (supportsLength) inputs.frames = clipFrames(seconds);
      if (supportsFps) inputs.fps = fps;

      const task = await createTask({ task_id: newTaskId(), alias: alias.name, inputs });
      // Stay here: the run joins the strip under the sheet, and the same
      // photos can go again with another prompt or setting.
      const what = model === IMAGE_TO_VIDEO && target ? `${target.grade} · ${seconds} s · ${fps} fps` : alias.name;
      setRuns((prev) => [
        ...prev,
        {
          taskId: task.task_id,
          signature,
          createdAt: new Date().toISOString(),
          label: `${what} · ${results} result${results === 1 ? "" : "s"}`,
          imageIds,
          results,
        },
      ]);
      await refresh();
    } catch (e) {
      setRunError(e instanceof Error ? e.message : "failed to create task");
    } finally {
      setRunning(false);
    }
  }

  const canRun =
    !!alias && photoCount > 0 && selectedReady && !running && !alreadyRun && (!promptRequired || hint.trim() !== "");
  const shortOfBalance = !!user && !!quote.quote && quote.quote.total > user.balance;

  return (
    <div className="u-page">
      <div className="u-head">
        <h1 className="u-h1">Generate</h1>
        <p className="u-lede">
          Add photos, keep the ones you want in this run, and choose how many variations of each.
        </p>
      </div>
      {aliasesError && <p className="u-error">{aliasesError}</p>}

      <div className="u-bench">
        <section>
          <div
            className={dragging ? "u-drop u-drop--over" : "u-drop"}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
          >
            <span className="u-drop__hint">Drop photos here</span>
            <span className="u-drop__sub">
              one or many — each becomes a row of the contact sheet below
            </span>
            <label className="u-btn">
              Choose files
              <input
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => e.target.files && addFiles(e.target.files)}
              />
            </label>
          </div>

          {steps.length > 0 && (
            <>
              <div className="u-sheet__bar">
                <span className="u-sheet__count">
                  {photoCount} of {steps.length} in the batch
                </span>
                <span className="u-sheet__spacer" />
                <button className="u-btn u-btn--quiet" onClick={() => setAll(true)}>
                  All
                </button>
                <button className="u-btn u-btn--quiet" onClick={() => setAll(false)}>
                  None
                </button>
                <button
                  className="u-btn u-btn--quiet"
                  onClick={onClear}
                  title="Start over: remove every photo, the prompt and the runs strip"
                >
                  Clear
                </button>
              </div>

              <ul className="u-grid" data-testid="upload-list">
                {steps.map((s) => {
                  const on = inBatch(s);
                  const url = previewURLs[s.index] ?? (s.artifactId ? previewURLs[s.artifactId] : undefined);
                  return (
                    <li
                      key={s.index}
                      className={
                        "u-frame" +
                        (on ? " u-frame--on" : "") +
                        (s.status === "uploading" ? " u-frame--printing" : "") +
                        (s.status === "error" ? " u-frame--failed" : "")
                      }
                    >
                      <button
                        className="u-frame__stage"
                        aria-pressed={on}
                        onClick={() => toggle(s.index)}
                        title={on ? "Take out of the batch" : "Put in the batch"}
                      >
                        {url ? <img src={url} alt="" /> : <span className="u-frame__name">no preview</span>}
                        {s.status === "uploading" && <span className="u-frame__scan" />}
                        {s.status === "pending" && <span className="u-frame__pending">not sent</span>}
                      </button>
                      <button
                        className="u-frame__remove"
                        onClick={() => removePhoto(s.index)}
                        aria-label={`Remove ${s.file.name || `photo ${s.index + 1}`}`}
                        title="Remove this photo"
                      >
                        ×
                      </button>
                      <div className="u-frame__cap">
                        <span className="u-frame__name" title={s.file.name}>
                          {s.file.name || `photo ${s.index + 1}`}
                        </span>
                        <span className="u-frame__meta">
                          {s.status === "done" ? (
                            "sent"
                          ) : s.status === "error" ? (
                            <button className="u-linkbtn u-state--error" onClick={() => retryPhoto(s.index)}>
                              failed · retry
                            </button>
                          ) : s.status === "uploading" ? (
                            "sending…"
                          ) : (
                            "waiting"
                          )}
                        </span>
                      </div>
                      {s.error && <p className="u-error">{s.error}</p>}
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          <RunStrip runs={runs} />
        </section>

        <aside className="u-console">
          <label className="u-field">
            Model
            <select className="u-select" value={model} onChange={(e) => setModel(e.target.value)}>
              {models.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>

          {model === IMAGE_TO_VIDEO && grades.length > 0 && (
            <label className="u-field">
              Resolution
              <select className="u-input" value={grade} onChange={(e) => setGrade(e.target.value)}>
                {grades.map((g) => (
                  <option key={g.grade} value={g.grade}>
                    {g.grade} · {g.width}×{g.height}
                  </option>
                ))}
              </select>
            </label>
          )}

          {targets.length > 0 && (
            <label className="u-field">
              Duration
              <select className="u-input" value={seconds} onChange={(e) => setSeconds(Number(e.target.value))}>
                {lengths.map((s) => (
                  <option key={s} value={s}>
                    {s} s
                  </option>
                ))}
              </select>
            </label>
          )}

          {targets.length > 0 && (
            <label className="u-field">
              Frame rate
              <select className="u-input" value={fps} onChange={(e) => setFps(Number(e.target.value))}>
                {rates.map((f) => (
                  <option key={f} value={f}>
                    {f} fps
                  </option>
                ))}
              </select>
            </label>
          )}

          {supportsPrompt && (
            <label className="u-field">
              {promptRequired || defaultPrompt ? "Prompt" : "Hint"}{" "}
              {!promptRequired && (
                <span className="u-note">{enhancing
                    ? "optional, written from the photo if empty"
                    : defaultPrompt
                      ? "optional, the placeholder runs if empty"
                      : "optional"}</span>
              )}
              <textarea
                className="u-input u-textarea"
                rows={4}
                value={hint}
                onChange={(e) => setHint(e.target.value)}
                placeholder={enhancing ? ENHANCE_PLACEHOLDER : defaultPrompt || "slow zoom, dramatic lighting"}
              />
            </label>
          )}

          {supportsEnhance && (
            <label className="u-check" data-testid="enhance">
              <input type="checkbox" checked={enhance} onChange={(e) => setEnhance(e.target.checked)} />
              Enhance prompt
              <span className="u-note">rewrites it from the photo before the clip runs</span>
            </label>
          )}

          {supportsPhotoAt && (
            <label className="u-field">
              Photo at
              <select className="u-input" value={photoAt} onChange={(e) => setPhotoAt(e.target.value as PhotoAt)}>
                <option value="start">Start: the clip begins on the photo</option>
                <option value="middle">Middle: the clip passes through the photo</option>
                <option value="end">End: the clip arrives at the photo</option>
              </select>
            </label>
          )}


          {avail && (
            <div className={`u-avail u-avail--${avail.tone}`} data-testid="availability" role="status">
              <span className="u-avail__dot" aria-hidden="true" />
              <span className="u-avail__title">{avail.title}</span>
              <span className="u-avail__detail">{avail.detail}</span>
            </div>
          )}

          {target && (
            <div className="u-estimate" data-testid="target-estimate">
              <div>
                ready in ~{formatWait(target.estimate.cold_wait_s)} from a cold start, ~
                {formatWait(target.estimate.warm_wait_s)} on a warm worker
                {target.estimate.basis === "inferred" && <span className="u-note"> estimate</span>}
              </div>
              {catalog?.costs && target.estimate.cold_cost_minor !== undefined && (
                <div className="u-note" data-testid="target-cost">
                  our cost ~{formatCost(target.estimate.cold_cost_minor, catalog.currency)} cold ·{" "}
                  {formatCost(target.estimate.warm_cost_minor ?? 0, catalog.currency)} warm · {target.estimate.gpu}
                  {target.estimate.usd_per_hour !== undefined && ` $${target.estimate.usd_per_hour.toFixed(2)}/h`}
                </div>
              )}
            </div>
          )}

          {supportsLength && targets.length === 0 && (
            <label className="u-field">
              Length
              <select className="u-input" value={seconds} onChange={(e) => setSeconds(Number(e.target.value))}>
                {CLIP_SECONDS.map((s) => (
                  <option key={s} value={s}>
                    {s} s
                  </option>
                ))}
              </select>
            </label>
          )}

          {supportsFps && targets.length === 0 && (
            <label className="u-field">
              Frame rate
              <select className="u-input" value={fps} onChange={(e) => setFps(Number(e.target.value))}>
                {CLIP_FPS.map((f) => (
                  <option key={f} value={f}>
                    {f} fps
                  </option>
                ))}
              </select>
            </label>
          )}

          {supportsSeed && (
            <label className="u-field">
              Variations of each photo <span className="u-num">{variants}</span>
              <input
                className="u-slider"
                type="range"
                min={1}
                max={10}
                value={variants}
                onChange={(e) => setVariants(Number(e.target.value))}
              />
            </label>
          )}

          <div className="u-quote">
            <div className="u-quote__count">
              {photoCount} photo{photoCount === 1 ? "" : "s"} x {effectiveVariants} variant
              {effectiveVariants === 1 ? "" : "s"} = {results} result{results === 1 ? "" : "s"}
            </div>
            <div className="u-quote__price">
              {quote.quote ? formatMinor(quote.quote.total, quote.quote.currency) : results > 0 ? "…" : "—"}
            </div>
            {quote.stale && quote.quote && <span className="u-quote__stale">updating…</span>}
            {user && (
              <div className="u-quote__sub">
                balance <span className="u-num">{formatMinor(user.balance, user.currency)}</span>
              </div>
            )}
            {shortOfBalance && <p className="u-error">insufficient balance for this batch</p>}
          </div>

          {runError && <p className="u-error">{runError}</p>}

          {uploading && (
            <div className="u-print">
              <div className="u-bar">
                <span
                  className="u-bar__fill"
                  style={{ width: `${steps.length ? Math.round((uploadedCount / steps.length) * 100) : 0}%` }}
                />
                <span className="u-bar__stripes" />
              </div>
              <p className="u-console__label">
                sending photo {uploadedCount + 1} of {steps.length}
              </p>
            </div>
          )}

          <button className="u-btn u-btn--solid u-btn--wide" disabled={!canRun} onClick={() => void onRun()}>
            {running ? "Starting…" : "Run"}
          </button>
          {alreadyRun && (
            <p className="u-note" data-testid="already-run">
              Already run with exactly these photos and settings. Change the prompt, the selection or a setting
              to run again.
            </p>
          )}
        </aside>
      </div>
    </div>
  );
}
